/** @typedef {{ tagline: string, body: string, paths: string }} InterestClusterPopupEntry */

/** @type {Record<'en'|'tc'|'sc', Record<number, InterestClusterPopupEntry>>} */
export const INTEREST_CLUSTER_POPUP = {
  en: {
    0: {
      tagline: 'Help people and strengthen the communities around you.',
      body: 'Social work suits students who care about fairness and real-life problems. You learn to support families, youth, and vulnerable groups alongside NGOs and public services. If you like listening, problem-solving, and making a practical difference, this path could fit you well.',
      paths: 'Bachelor of Social Work · Community outreach roles · NGO and public-sector programmes'
    },
    1: {
      tagline: 'Understand why people think, feel, and act the way they do.',
      body: 'Psychology and social science explore human behaviour, relationships, and society. You build research skills while learning how to support wellbeing and analyse social issues. This area suits curious minds who enjoy evidence, empathy, and asking thoughtful questions.',
      paths: 'Bachelor of Psychology · Social science research · Counselling and human-services pathways'
    },
    2: {
      tagline: 'Design and build the infrastructure that cities rely on.',
      body: 'Civil engineering turns maths and physics into roads, bridges, water systems, and sustainable structures. You work on real projects that improve safety and everyday life. If you like solving large-scale problems with precision and teamwork, this field is worth exploring.',
      paths: 'Bachelor of Civil Engineering · Structural and geotechnical engineering · Construction and infrastructure planning'
    },
    3: {
      tagline: 'Explore big ideas about the past, meaning, and how we live.',
      body: 'History and philosophy train you to read critically, argue clearly, and understand how societies change over time. You develop strong writing and reasoning skills that transfer across many careers. This path fits students who enjoy debate, culture, and deep thinking.',
      paths: 'Bachelor of Arts in History or Philosophy · Heritage and museum studies · Policy, education, and research roles'
    },
    4: {
      tagline: 'Help people move, recover, and live more comfortably every day.',
      body: 'Physiotherapy and therapy focus on rehabilitation, injury recovery, and improving physical function. You combine science with hands-on care and long-term patient support. If you want a health career built on patience, observation, and helping others regain independence, look here.',
      paths: 'Bachelor of Physiotherapy · Occupational therapy programmes · Sports rehabilitation and clinical therapy roles'
    },
    5: {
      tagline: 'Create sound, visuals, and digital experiences that move people.',
      body: 'Music and digital arts blend creativity with technology—from composition and performance to animation and interactive media. You learn to express ideas through both artistic craft and modern tools. This suits students who enjoy making, experimenting, and sharing their work.',
      paths: 'Bachelor of Music or Creative Media · Digital content production · Audio, animation, and interactive design pathways'
    },
    6: {
      tagline: 'Shape how young children learn, play, and grow with confidence.',
      body: 'Early childhood education prepares you to support learning in the most formative years. You study child development, classroom practice, and how to create safe, engaging environments. If you are patient, energetic, and motivated by helping kids thrive, this area may be a strong match.',
      paths: 'Bachelor of Education (Early Childhood) · Kindergarten teaching qualifications · Child development and family-support roles'
    },
    7: {
      tagline: 'Turn ideas into useful products, systems, and user-friendly designs.',
      body: 'Technology and design sits at the crossroads of engineering, creativity, and problem-solving. You may work on apps, devices, services, or experiences that people use every day. This path fits students who like prototyping, improving how things work, and thinking from the user\'s point of view.',
      paths: 'Bachelor of Product Design or Innovation · UX and interaction design · Technology entrepreneurship programmes'
    },
    8: {
      tagline: 'Ask questions about the world and discover answers through research.',
      body: 'Science and research covers fields like biology, chemistry, and physics, with a focus on experiments and evidence. You build analytical skills that support labs, industry, and further study. If you enjoy curiosity, careful method, and uncovering how things work, this cluster aligns well.',
      paths: 'Bachelor of Science · Laboratory and research assistant roles · Honours and postgraduate research pathways'
    },
    9: {
      tagline: 'Use numbers and models to explain how economies and decisions work.',
      body: 'Economics and maths train you to analyse data, trends, and the trade-offs behind real-world choices. You develop logical thinking that supports finance, policy, and business roles. Students who enjoy puzzles, graphs, and structured reasoning often feel at home here.',
      paths: 'Bachelor of Economics or Mathematics · Actuarial and quantitative analysis · Policy and business analytics programmes'
    },
    10: {
      tagline: 'Care for patients with skill, compassion, and steady teamwork.',
      body: 'Nursing and health care combine clinical knowledge with day-to-day support for people in hospitals and community settings. You learn practical skills while working closely with doctors, patients, and families. If you want a hands-on health career with clear impact, this is a strong option.',
      paths: 'Bachelor of Nursing · Registered nurse training · Community health and clinical care roles'
    },
    11: {
      tagline: 'Connect products and ideas with the people who need them.',
      body: 'Business and marketing is about understanding customers, building brands, and communicating value. You learn strategy, creativity, and how markets respond to new offerings. This suits students who enjoy persuasion, storytelling, and spotting opportunities in everyday life.',
      paths: 'Bachelor of Business Administration · Marketing and brand management · Sales, digital marketing, and market research roles'
    },
    12: {
      tagline: 'Create memorable experiences in travel, food, and guest services.',
      body: 'Hospitality and tourism focuses on hotels, events, restaurants, and destinations that welcome visitors from around the world. You develop service skills, cultural awareness, and operational know-how. If you enjoy people, travel, and making others feel welcome, this field offers many paths.',
      paths: 'Bachelor of Hospitality Management · Hotel and event operations · Tourism planning and culinary management programmes'
    },
    13: {
      tagline: 'Keep organisations running smoothly and teams working well together.',
      body: 'Management and operations is about planning, logistics, quality, and leading people through complex work. You learn how businesses deliver products and services efficiently at scale. Students who like organising, improving systems, and coordinating others often thrive here.',
      paths: 'Bachelor of Business Management · Operations and supply chain programmes · Project management and leadership roles'
    },
    14: {
      tagline: 'Shape spaces where people live, work, and move through cities.',
      body: 'Architecture and urban design blends art, engineering, and planning to create buildings and neighbourhoods. You think about function, sustainability, and how places feel to the people who use them. If you enjoy drawing, spatial thinking, and long-term creative projects, explore this area.',
      paths: 'Bachelor of Architecture · Urban planning and landscape design · Built-environment and sustainable design programmes'
    },
    15: {
      tagline: 'Build the hardware and systems that power modern computing.',
      body: 'Computer engineering connects electronics, software, and embedded systems in devices we use every day. You learn to design circuits, write low-level code, and solve technical problems at scale. This path suits students who like both hands-on building and precise logical thinking.',
      paths: 'Bachelor of Computer Engineering · Embedded systems and robotics · Hardware and systems development roles'
    },
    16: {
      tagline: 'Navigate rules, contracts, and business decisions with confidence.',
      body: 'Law and business combines legal reasoning with commercial awareness—from compliance and dispute resolution to corporate strategy. You develop strong reading, argument, and negotiation skills. If you enjoy structured debate and understanding how organisations operate within the law, this cluster fits.',
      paths: 'Bachelor of Laws or Law and Business · Legal executive and paralegal pathways · Corporate compliance and business law roles'
    },
    17: {
      tagline: 'Work across borders, cultures, and international markets.',
      body: 'Global business prepares you for trade, supply chains, and leadership in an connected world. You study how companies expand overseas and adapt to different markets. Students with curiosity about other cultures and an interest in strategy often find this area exciting.',
      paths: 'Bachelor of International Business · Global supply chain management · Export, trade, and regional business development roles'
    },
    18: {
      tagline: 'Use lab science to support diagnosis and patient care behind the scenes.',
      body: 'Medical laboratory science focuses on testing samples, analysing results, and helping clinicians make informed decisions. You work with precision equipment and strict quality standards. If you prefer lab-based health work over direct bedside care, this is a focused and in-demand path.',
      paths: 'Bachelor of Medical Laboratory Science · Clinical laboratory technologist training · Diagnostic and biomedical testing roles'
    },
    19: {
      tagline: 'Help others learn languages and communicate across cultures.',
      body: 'Language education trains you to teach reading, writing, speaking, and listening in school or adult settings. You combine language mastery with classroom methods and learner support. This suits students who enjoy explaining ideas clearly and celebrating progress in others.',
      paths: 'Bachelor of Education (Language) · Teaching English or Chinese as a second language · Curriculum and language-centre roles'
    },
    20: {
      tagline: 'Track money, manage records, and keep organisations financially healthy.',
      body: 'Accounting is about measuring performance, reporting accurately, and supporting sound financial decisions. You build skills in analysis, audit, and professional standards that employers value highly. If you are detail-oriented and comfortable with numbers and rules, accounting offers stable career options.',
      paths: 'Bachelor of Accountancy · Professional accounting qualification pathways · Audit, tax, and financial reporting roles'
    },
    21: {
      tagline: 'Train bodies and minds through sport, fitness, and coaching.',
      body: 'Sports and coaching combines exercise science with motivation, teamwork, and performance improvement. You may work with athletes, schools, clubs, or community fitness programmes. Students who enjoy movement, discipline, and helping others reach goals often feel energised by this field.',
      paths: 'Bachelor of Sports Science or Physical Education · Coaching and fitness instruction · Sports management and athletic development roles'
    },
    22: {
      tagline: 'Turn data into insights and build the software that runs on it.',
      body: 'Data and computing covers programming, databases, analytics, and the systems organisations rely on daily. You learn to solve problems with code while making sense of large datasets. If you enjoy logic, experimentation, and building digital tools, this is one of the most flexible paths available.',
      paths: 'Bachelor of Computer Science · Data analytics and information systems · Software development and IT support roles'
    },
    23: {
      tagline: 'Study health in depth and support treatment from clinic to pharmacy.',
      body: 'Medicine and pharmacy paths demand strong science foundations and a commitment to patient wellbeing over many years of study. You may diagnose illness, prescribe treatment, or ensure medicines are safe and effective. This cluster suits students ready for rigorous training and high-responsibility careers.',
      paths: 'Bachelor of Medicine or Pharmacy · Pre-clinical health science programmes · Clinical, pharmaceutical, and healthcare specialist training'
    },
    24: {
      tagline: 'Manage money, risk, and the technology reshaping financial services.',
      body: 'Finance and FinTech blends investment, banking, and digital tools like mobile payments and blockchain. You learn to evaluate risk, model returns, and understand how markets move. Students who enjoy fast-moving numbers, strategy, and innovation often gravitate toward this area.',
      paths: 'Bachelor of Finance or Financial Technology · Investment and risk management · Banking, wealth management, and FinTech product roles'
    },
    25: {
      tagline: 'Deepen your grasp of Chinese language, culture, and the humanities.',
      body: 'Chinese and humanities explores literature, history, language, and cultural traditions in depth. You develop strong reading, writing, and interpretive skills valued in education, media, and public life. If you enjoy classical and modern texts and reflecting on identity and society, this path may resonate.',
      paths: 'Bachelor of Chinese Language and Literature · Cultural studies and humanities research · Publishing, education, and cultural programme roles'
    },
    26: {
      tagline: 'Apply life science to improve health, diagnostics, and medical research.',
      body: 'Biomedical and health science bridges biology with medicine—covering genetics, disease mechanisms, and health technologies. You may work in research labs, diagnostics, or health-industry roles. Curious students who like lab work and want to contribute to better health outcomes often fit here.',
      paths: 'Bachelor of Biomedical Science · Clinical research and laboratory roles · Public health and health-science postgraduate pathways'
    },
    27: {
      tagline: 'Protect ecosystems and tackle climate and sustainability challenges.',
      body: 'Environmental science studies air, water, land, and biodiversity—and how human activity affects them. You combine fieldwork, data, and policy awareness to propose practical solutions. If you care about the planet and enjoy science with real-world impact, this field offers meaningful work.',
      paths: 'Bachelor of Environmental Science · Conservation and sustainability programmes · Environmental consulting and policy support roles'
    },
    28: {
      tagline: 'Study society and help shape fairer policies and public decisions.',
      body: 'Sociology and public policy examines inequality, institutions, and how governments respond to social needs. You learn research methods and how to evaluate programmes that affect communities. Students who care about social justice and evidence-based change often find this area compelling.',
      paths: 'Bachelor of Sociology or Public Policy · Social research and programme evaluation · Government, NGO, and advocacy roles'
    },
    29: {
      tagline: 'Tell stories, share messages, and reach audiences across platforms.',
      body: 'Media and communication covers journalism, public relations, digital content, and how information spreads in society. You develop writing, production, and strategic communication skills. If you enjoy creativity, current affairs, and connecting with people through words and visuals, explore here.',
      paths: 'Bachelor of Journalism or Communication · Public relations and content strategy · Broadcasting, social media, and corporate communications roles'
    },
    30: {
      tagline: 'Express ideas through visual art, film, and creative production.',
      body: 'Fine arts and film trains you in drawing, painting, sculpture, cinematography, and storytelling through image and motion. You build a portfolio while learning craft, critique, and collaboration. This path suits students who think visually and want room to experiment and develop a personal voice.',
      paths: 'Bachelor of Fine Arts or Film · Visual arts and cinematography programmes · Gallery, studio, and creative production roles'
    },
    31: {
      tagline: 'Merge engineering with medicine to build health technologies and devices.',
      body: 'Biomedical engineering designs implants, imaging tools, prosthetics, and other systems that support diagnosis and treatment. You apply maths, biology, and engineering to real clinical problems. Students who like inventing practical solutions at the frontier of health and technology often thrive here.',
      paths: 'Bachelor of Biomedical Engineering · Medical device and imaging technology · Rehabilitation engineering and health-tech research roles'
    },
    32: {
      tagline: 'Design complex systems that work reliably at scale.',
      body: 'Systems engineering focuses on integrating parts—people, hardware, software, and processes—into dependable whole solutions. You think about performance, safety, and lifecycle from start to finish. If you enjoy big-picture planning and making many moving pieces work together, this field is a strong fit.',
      paths: 'Bachelor of Systems Engineering · Industrial and systems design · Aerospace, transport, and large-scale project engineering roles'
    },
    33: {
      tagline: 'Build intelligent systems that learn, predict, and automate decisions.',
      body: 'Artificial intelligence applies maths, computing, and data to machines that recognise patterns and assist human work. You may work on machine learning, natural language, or robotics applications. Students who enjoy coding, experimentation, and cutting-edge technology often find this one of the most dynamic paths.',
      paths: 'Bachelor of Artificial Intelligence or Computer Science · Machine learning and data science programmes · AI product, research, and automation engineering roles'
    },
    34: {
      tagline: 'Master languages and open doors across cultures and careers.',
      body: 'English and languages develops advanced reading, writing, translation, and cross-cultural communication skills. You may study linguistics, literature, or professional language use for global workplaces. If you enjoy words, nuance, and connecting with people from different backgrounds, this cluster offers wide options.',
      paths: 'Bachelor of English or Modern Languages · Translation and interpretation programmes · International relations, education, and publishing roles'
    }
  },
  tc: {
    0: {
      tagline: '幫到人，仲可以令社區變得更好。',
      body: '社會工作適合關心公平同現實問題嘅同學。你會學點樣支援家庭、青年同弱勢社群，同 NGO 同公營機構一齊做實事。如果你鍾意聆聽、解決問題，又想見到實際改變，呢個方向可能真係啱你。',
      paths: '社會工作學士 · 社區服務工作 · NGO 同公營機構相關課程'
    },
    1: {
      tagline: '了解人點解會咁諗、咁Feel、咁做。',
      body: '心理學同社會科學研究人嘅行為、關係同社會現象。你會建立研究能力，同時學點樣關顧身心同分析社會議題。如果你好奇、有同理心，又鍾意用證據答問題，呢個領域值得睇。',
      paths: '心理學學士 · 社會科學研究 · 輔導同人力服務相關出路'
    },
    2: {
      tagline: '設計同建造城市靠得住嘅基建。',
      body: '土木工程將數學同物理變成道路、橋樑、供水系統同可持續結構。你會參與真實工程，改善安全同日常生活。如果你鍾意解決大型問題，又重視精準同團隊合作，可以認真考慮呢個方向。',
      paths: '土木工程學士 · 結構同岩土工程 · 建造同基建規劃相關課程'
    },
    3: {
      tagline: '探索歷史、意義同我哋點樣生活。',
      body: '歷史同哲學訓練你批判閱讀、清晰論證，同理解社會點樣隨時間改變。你會練到好強嘅寫作同推理能力，轉行都幾容易。如果你鍾意辯論、文化同深度思考，呢條路可能好啱。',
      paths: '歷史或哲學文學士 · 文化遺產同博物館研究 · 政策、教育同研究相關工作'
    },
    4: {
      tagline: '幫人恢復活動能力，生活舒服啲。',
      body: '物理治療同復康專注於康復、受傷恢復同改善身體功能。你會結合科學知識同實際照顧，長期支援病人。如果你想做有溫度嘅健康工作，又耐得性、觀察力夠，可以留意呢個方向。',
      paths: '物理治療學士 · 職業治療課程 · 運動復康同臨床治療相關出路'
    },
    5: {
      tagline: '用聲音、影像同數碼創作打動人。',
      body: '音樂同數碼藝術結合創意同科技——由作曲演出到動畫同互動媒體都有。你會學用藝術技巧同現代工具表達想法。如果你鍾意創作、試新嘢同分享作品，呢個領域好有發揮空間。',
      paths: '音樂或創意媒體學士 · 數碼內容製作 · 音響、動畫同互動設計相關課程'
    },
    6: {
      tagline: '喺最重要嘅年紀，陪小朋友學習同成長。',
      body: '幼兒教育準備你喺最關鍵嘅階段支援學習。你會讀兒童發展、課堂實務，同點樣營造安全又好玩嘅環境。如果你夠耐性、有精力，又想見到細路仔進步，呢個方向可能好啱你。',
      paths: '幼兒教育學士 · 幼稚園教師資格 · 兒童發展同家庭支援相關工作'
    },
    7: {
      tagline: '將意念變成有用嘅產品、系統同設計。',
      body: '科技同設計橫跨工程、創意同解難——可能係 App、裝置、服務或者日常體驗。你會學原型製作，同點樣由用家角度改善事物。如果你鍾意試嘢、改善流程，呢條路值得探索。',
      paths: '產品設計或創新科技學士 · 用戶體驗同互動設計 · 科技創業相關課程'
    },
    8: {
      tagline: '用研究搵答案，了解世界點運作。',
      body: '科學同研究涵蓋生物、化學、物理等，重視實驗同證據。你會建立分析能力，支援實驗室、業界同進修。如果你好奇、重視方法，又想知事物點運作，呢個群組好啱。',
      paths: '理學士 · 實驗室同研究助理 · 榮譽學位同研究院進修'
    },
    9: {
      tagline: '用數字同模型解釋經濟同決策點運作。',
      body: '經濟學同數學訓練你分析數據、趨勢同現實選擇背後嘅取捨。你會練到好強嘅邏輯思維，支援金融、政策同商業工作。如果你鍾意解謎、睇圖表同有條理嘅推理，呢度可能好啱。',
      paths: '經濟學或數學學士 · 精算同量化分析 · 政策同商業分析相關課程'
    },
    10: {
      tagline: '用專業同關懷，喺前線照顧病人。',
      body: '護理同醫療保健結合臨床知識同日常病人支援，喺醫院同社區都會用到。你會學實用技能，同醫生、病人同家人密切合作。如果你想做有即時影響嘅健康工作，呢個係好強嘅選擇。',
      paths: '護理學士 · 註冊護士培訓 · 社區健康同臨床護理相關出路'
    },
    11: {
      tagline: '將產品同意念，連到真正需要嘅人。',
      body: '商業同市場推廣係了解顧客、建立品牌同傳達價值。你會學策略、創意同市場對新產品嘅反應。如果你鍾意說服、講故事，又擅長發現機會，呢個方向可能啱你。',
      paths: '工商管理學士 · 市場推廣同品牌管理 · 銷售、數碼營銷同市場研究相關工作'
    },
    12: {
      tagline: '喺旅遊、飲食同服務行業創造難忘體驗。',
      body: '酒店同旅遊專注酒店、活動、餐飲同接待世界各地旅客嘅目的地。你會建立服務技巧、文化敏感度同營運知識。如果你鍾意同人相處、愛旅行，又想令客人有賓至如歸嘅感覺，呢個領域選擇好多。',
      paths: '酒店管理學士 · 酒店同活動營運 · 旅遊規劃同廚藝管理相關課程'
    },
    13: {
      tagline: '令機構順暢運作，團隊合作更順。',
      body: '管理同營運關乎規劃、物流、品質同帶領團隊完成複雜工作。你會學企業點樣有效率咁提供產品同服務。如果你鍾意整理、改善系統同協調他人，呢度往往好啱。',
      paths: '工商管理學士 · 營運同供應鏈課程 · 項目管理同領導相關出路'
    },
    14: {
      tagline: '設計人生活、工作同活動嘅空間。',
      body: '建築同城市設計結合藝術、工程同規劃，塑造建築同社區。你要諗功能、可持續性，同地方俾使用者嘅感覺。如果你鍾意繪圖、空間思維同長期創作項目，可以探索呢個方向。',
      paths: '建築學學士 · 城市規劃同景觀設計 · 建成環境同可持續設計相關課程'
    },
    15: {
      tagline: '建造驅動現代運算嘅硬件同系統。',
      body: '計算機工程連接電子、軟件同嵌入式系統，支撐日常裝置。你會學設計電路、編寫底層程式同解決技術難題。如果你鍾意動手做嘢，又有精準邏輯思維，呢條路值得睇。',
      paths: '計算機工程學士 · 嵌入式系統同機械人 · 硬件同系統開發相關工作'
    },
    16: {
      tagline: '掌握規則、合約同商業決策嘅要害。',
      body: '法律同商業結合法律推理同商業觸覺——由合規、爭議解決到企業策略都有。你會練閱讀、論證同談判能力。如果你鍾意有結構嘅辯論，又想了解機構點樣喺法律框架內運作，呢個群組啱晒。',
      paths: '法律或法律與商業學士 · 法律行政同律師助理 · 企業合規同商業法律相關出路'
    },
    17: {
      tagline: '跨國界、跨文化，喺國際市場發展。',
      body: '全球商業準備你應對貿易、供應鏈同全球化領導。你會了解企業點樣拓展海外同適應唔同市場。如果你對其他文化好奇，又對策略有興趣，呢個領域通常好吸引。',
      paths: '國際商業學士 · 全球供應鏈管理 · 出口貿易同區域業務拓展相關工作'
    },
    18: {
      tagline: '用實驗室科學支援診斷同幕後醫療。',
      body: '醫務化驗科學專注檢測樣本、分析結果，幫臨床人員做決定。你要操作精密儀器，遵守嚴格品質標準。如果你偏好實驗室健康工作多過床邊照顧，呢條路專注又需求大。',
      paths: '醫務化驗科學學士 · 臨床化驗技師培訓 · 診斷同生物醫學檢測相關出路'
    },
    19: {
      tagline: '教語言，幫人跨文化溝通。',
      body: '語言教育訓練你喺學校或成人環境教閱讀、寫作、說話同聆聽。你會結合語言能力、教學方法同學習支援。如果你鍾意清楚解釋，又享受見到他人進步，呢個方向可能好啱。',
      paths: '語言教育學士 · 英語或中文作第二語言教學 · 課程設計同語言中心相關工作'
    },
    20: {
      tagline: '記錄財務、把關數字，令機構穩健運作。',
      body: '會計係衡量表現、準確申報同支援穩健財務決定。你會建立分析、審計同專業準則方面嘅能力，雇主好重視。如果你細心，又唔怕數字同規則，會計提供相當穩定嘅出路。',
      paths: '會計學士 · 專業會計資格進修 · 審計、稅務同財務申報相關工作'
    },
    21: {
      tagline: '用運動、體能同教練工作訓練身心。',
      body: '體育同教練結合運動科學、動力、團隊合作同表現提升。你可能同運動員、學校、球會或社區健身計劃合作。如果你鍾意郁、有紀律，又想幫人達標，呢個領域好有衝勁。',
      paths: '運動科學或體育教育學士 · 教練同健身指導 · 體育管理同運動員發展相關出路'
    },
    22: {
      tagline: '將數據變洞察，用軟件解決問題。',
      body: '數據同運算涵蓋程式編寫、數據庫、分析同機構日常依賴嘅系統。你會用程式解難，同時理解大型數據集。如果你鍾意邏輯、試驗同整數碼工具，呢係最靈活嘅方向之一。',
      paths: '計算機科學學士 · 數據分析同資訊系統 · 軟件開發同 IT 支援相關工作'
    },
    23: {
      tagline: '深入學健康，由診症到配藥都支援治療。',
      body: '醫學同藥學需要紮實科學基礎，同長期投入病人福祉。你可能診斷疾病、處方治療，或者確保藥物安全有效。如果你準備好接受嚴格訓練同高責任工作，呢個群組值得認真考慮。',
      paths: '醫學或藥學學士 · 臨床前健康科學課程 · 臨床、藥物同醫療專科培訓'
    },
    24: {
      tagline: '管理金錢、風險，同重塑金融嘅科技。',
      body: '金融同 FinTech 結合投資、銀行同流動支付、區塊鏈等數碼工具。你會學評估風險、建模回報同理解市場變化。如果你鍾意快節奏數字、策略同創新，往往會偏向呢個領域。',
      paths: '金融或金融科技學士 · 投資同風險管理 · 銀行、財富管理同 FinTech 產品相關工作'
    },
    25: {
      tagline: '深化中文、文化同人文素養。',
      body: '中文同人文探索文學、歷史、語言同文化傳統。你會建立強閱讀、寫作同詮釋能力，喺教育、媒體同公共生活都好有用。如果你鍾意古典同現代文本，又關心身份同社會，呢條路可能好啱。',
      paths: '中國語言文學學士 · 文化研究同人文研究 · 出版、教育同文化項目相關工作'
    },
    26: {
      tagline: '用生命科學改善健康、診斷同醫學研究。',
      body: '生物醫學同健康科學連接生物同醫學——涵蓋基因、疾病機制同健康科技。你可能喺研究實驗室、診斷或健康產業工作。如果你好奇、鍾意做實驗，又想為更好健康出力，呢度好啱。',
      paths: '生物醫學科學學士 · 臨床研究同實驗室工作 · 公共衛生同健康科學研究院進修'
    },
    27: {
      tagline: '保護生態，應對氣候同可持續發展挑戰。',
      body: '環境科學研究空氣、水、土地同生物多樣性，同人類活動嘅影響。你會結合野外工作、數據同政策意識，提出可行方案。如果你關心地球，又鍾意有實際影響嘅科學，呢個領域好有意義。',
      paths: '環境科學學士 · 保育同可持續發展課程 · 環境顧問同政策支援相關工作'
    },
    28: {
      tagline: '研究社會，推動更公平嘅公共決策。',
      body: '社會學同公共政策檢視不平等、制度同政府點回應社會需要。你會學研究方法，同點樣評估影響社群嘅計劃。如果你關心社會公義同循證改變，呢個領域往往好吸引。',
      paths: '社會學或公共政策學士 · 社會研究同計劃評估 · 政府、NGO 同倡議相關工作'
    },
    29: {
      tagline: '講故事、傳訊息，喺各平台接觸受眾。',
      body: '媒體同傳播涵蓋新聞、公關、數碼內容同資訊點喺社會流通。你會建立寫作、製作同策略溝通能力。如果你鍾意創意、時事，又想用文字同影像同人連結，可以探索呢度。',
      paths: '新聞或傳播學學士 · 公共關係同內容策略 · 廣播、社交媒體同企業傳訊相關工作'
    },
    30: {
      tagline: '用視覺藝術、電影同創作表達意念。',
      body: '純藝術同電影訓練你繪畫、雕塑、攝影同用影像同動態講故事。你會建立作品集，同時學技藝、評論同合作。如果你視覺感強，又想有空間試驗同發展個人風格，呢條路值得睇。',
      paths: '純藝術或電影學士 · 視覺藝術同攝影課程 · 畫廊、工作室同創作製作相關工作'
    },
    31: {
      tagline: '結合工程同醫學，研發健康科技同裝置。',
      body: '生物醫學工程設計植入物、影像工具、義肢等支援診斷同治療嘅系統。你會用數學、生物同工程解決臨床問題。如果你鍾意喺健康同科技前沿發明實用方案，呢度往往好啱。',
      paths: '生物醫學工程學士 · 醫療裝置同影像科技 · 復康工程同健康科技研究相關出路'
    },
    32: {
      tagline: '設計可靠、可擴展嘅複雜系統。',
      body: '系統工程專注將人、硬件、軟件同流程整合成穩定嘅整體方案。你要由開始到結束諗性能、安全同生命週期。如果你鍾意大局規劃，又擅長令好多環節一齊運作，呢個領域好啱。',
      paths: '系統工程學士 · 工業同系統設計 · 航空、運輸同大型工程項目相關工作'
    },
    33: {
      tagline: '建造會學習、預測同自動化決策嘅智能系統。',
      body: '人工智能運用數學、運算同數據，令機器識別模式同協助人類工作。你可能做機器學習、自然語言或者機械人應用。如果你鍾意寫程式、試驗同前沿科技，呢係最動感嘅方向之一。',
      paths: '人工智能或計算機科學學士 · 機器學習同數據科學課程 · AI 產品、研究同自動化工程相關工作'
    },
    34: {
      tagline: '掌握語言，打開跨文化同多元出路。',
      body: '英語同語言培養進階閱讀、寫作、翻譯同跨文化溝通能力。你可能讀語言學、文學或者職場專業語言運用。如果你鍾意文字、細微差別，又想同唔同背景嘅人連結，呢個群組選擇好闊。',
      paths: '英語或現代語言學士 · 翻譯同傳譯課程 · 國際關係、教育同出版相關出路'
    }
  },
  sc: {
    0: {
      tagline: '助人成长，共建更美好的社区。',
      body: '社会工作适合关注社会公平与现实问题的学生。您将学习如何支持家庭、青年及弱势群体，并与非政府组织及公共机构协作。若您善于倾听、乐于解决问题，并希望在实践中产生切实影响，此方向值得考虑。',
      paths: '社会工作学士 · 社区服务岗位 · 非政府组织及公共机构相关课程'
    },
    1: {
      tagline: '理解人们为何如此思考、感受与行动。',
      body: '心理学与社会科学研究人类行为、社会关系及社会现象。您将培养研究能力，并学习如何促进身心健康及分析社会议题。此领域适合具备好奇心、同理心，并重视以证据回答问题的学生。',
      paths: '心理学学士 · 社会科学研究 · 辅导及人力服务相关路径'
    },
    2: {
      tagline: '设计并建造城市所依赖的基础设施。',
      body: '土木工程将数学与物理应用于道路、桥梁、供水系统及可持续结构。您将参与真实工程项目，提升安全与生活质量。若您善于解决大型问题，并重视精确性与团队协作，此领域值得探索。',
      paths: '土木工程学士 · 结构及岩土工程 · 建造及基建规划相关课程'
    },
    3: {
      tagline: '探索历史、意义及人类生活方式。',
      body: '历史与哲学培养批判性阅读、清晰论证及理解社会变迁的能力。您将建立扎实的写作与推理技能，适用于多种职业方向。此路径适合喜爱辩论、文化及深度思考的学生。',
      paths: '历史或哲学文学士 · 文化遗产及博物馆研究 · 政策、教育及研究相关岗位'
    },
    4: {
      tagline: '帮助人们恢复活动能力，提升生活品质。',
      body: '物理治疗与康复专注于功能恢复、伤病康复及改善身体活动能力。您将结合科学知识与实践照护，长期支持患者。若您希望从事有温度的健康工作，并具备耐心与观察力，此方向较为合适。',
      paths: '物理治疗学士 · 职业治疗课程 · 运动康复及临床治疗相关路径'
    },
    5: {
      tagline: '以声音、影像及数字创作打动人心。',
      body: '音乐与数码艺术融合创意与科技，涵盖作曲演出、动画及互动媒体等领域。您将学习以艺术技巧与现代工具表达想法。此领域适合喜爱创作、勇于尝试并乐于分享作品的学生。',
      paths: '音乐或创意媒体学士 · 数字内容制作 · 音响、动画及互动设计相关课程'
    },
    6: {
      tagline: '在关键成长阶段引导儿童学习与发展。',
      body: '幼儿教育旨在支持儿童最早期的学习与发展。您将学习儿童发展、课堂实践及如何营造安全且富有吸引力的环境。若您耐心细致、精力充沛，并重视儿童成长，此方向可能较为匹配。',
      paths: '幼儿教育学士 · 幼儿园教师资格 · 儿童发展及家庭支持相关岗位'
    },
    7: {
      tagline: '将创意转化为实用的产品、系统与设计。',
      body: '科技与设计横跨工程、创意与问题解决，可能涉及应用程序、设备、服务或日常体验。您将学习原型制作及从用户角度改进事物的方法。此路径适合喜爱尝试、优化流程的学生。',
      paths: '产品设计或创新科技学士 · 用户体验及互动设计 · 科技创业相关课程'
    },
    8: {
      tagline: '以研究探索世界运作的方式。',
      body: '科学与研究涵盖生物、化学、物理等学科，重视实验与证据。您将建立分析能力，为实验室、产业及深造奠定基础。若您充满好奇心、重视方法，并渴望理解事物原理，此群组较为契合。',
      paths: '理学士 · 实验室及研究助理 · 荣誉学位及研究院进修'
    },
    9: {
      tagline: '以数字与模型解释经济及决策机制。',
      body: '经济学与数学培养数据分析、趋势研判及现实选择背后权衡的能力。您将建立逻辑思维能力，支持金融、政策及商业工作。若您喜爱解谜、图表及有条理的推理，此领域较为适合。',
      paths: '经济学或数学学士 · 精算及量化分析 · 政策及商业分析相关课程'
    },
    10: {
      tagline: '以专业与关怀在前线照顾患者。',
      body: '护理与医疗保健结合临床知识及日常患者支持，应用于医院及社区环境。您将学习实用技能，并与医生、患者及家属密切协作。若您希望从事具有即时影响的健康工作，此方向是稳健选择。',
      paths: '护理学士 · 注册护士培训 · 社区健康及临床护理相关路径'
    },
    11: {
      tagline: '连接产品与理念，触达真正需要的受众。',
      body: '商业与市场推广旨在理解顾客、建立品牌并传递价值。您将学习策略、创意及市场对新产品的反应。若您善于说服与叙事，并擅长发现机会，此方向值得考虑。',
      paths: '工商管理学士 · 市场推广及品牌管理 · 销售、数字营销及市场研究相关岗位'
    },
    12: {
      tagline: '在旅游、餐饮及服务领域创造难忘体验。',
      body: '酒店与旅游专注于酒店、活动、餐饮及接待各地旅客的目的地管理。您将培养服务技巧、文化敏感度及营运知识。若您喜爱与人互动及旅行，并重视宾客体验，此领域选择丰富。',
      paths: '酒店管理学士 · 酒店及活动营运 · 旅游规划及厨艺管理相关课程'
    },
    13: {
      tagline: '保障机构顺畅运作，促进团队协作。',
      body: '管理与营运涉及规划、物流、品质及带领团队完成复杂工作。您将了解企业如何高效提供产品与服务。若您善于组织、改善系统并协调他人，此领域往往较为合适。',
      paths: '工商管理学士 · 营运及供应链课程 · 项目管理及领导相关路径'
    },
    14: {
      tagline: '塑造人们居住、工作及活动的空间。',
      body: '建筑与城市设计结合艺术、工程及规划，塑造建筑与社区。您将思考功能、可持续性，以及空间对使用者的感受。若您喜爱绘图、空间思维及长期创作项目，可探索此方向。',
      paths: '建筑学学士 · 城市规划及景观设计 · 建成环境及可持续设计相关课程'
    },
    15: {
      tagline: '构建驱动现代运算的硬件与系统。',
      body: '计算机工程连接电子、软件及嵌入式系统，支撑日常设备运作。您将学习电路设计、底层编程及技术问题解决。若您既重视动手实践，又具备精确逻辑思维，此路径值得考虑。',
      paths: '计算机工程学士 · 嵌入式系统及机器人 · 硬件及系统开发相关岗位'
    },
    16: {
      tagline: '掌握规则、合约及商业决策的要害。',
      body: '法律与商业结合法律推理与商业意识，涵盖合规、争议解决及企业策略等领域。您将培养阅读、论证及谈判能力。若您喜爱结构化辩论，并关注机构如何在法律框架内运作，此群组较为契合。',
      paths: '法律或法律与商业学士 · 法律行政及律师助理 · 企业合规及商业法律相关路径'
    },
    17: {
      tagline: '跨越国界与文化，参与国际市场。',
      body: '全球商业培养贸易、供应链及全球化领导能力。您将了解企业如何拓展海外并适应不同市场。若您对多元文化充满好奇，并对策略感兴趣，此领域通常颇具吸引力。',
      paths: '国际商业学士 · 全球供应链管理 · 出口贸易及区域业务拓展相关岗位'
    },
    18: {
      tagline: '以实验室科学支持诊断及幕后医疗工作。',
      body: '医务化验科学专注于样本检测、结果分析及协助临床决策。您将操作精密仪器并遵循严格品质标准。若您偏好实验室健康工作而非床边照护，此路径专业且需求稳定。',
      paths: '医务化验科学学士 · 临床化验技师培训 · 诊断及生物医学检测相关路径'
    },
    19: {
      tagline: '教授语言，促进跨文化沟通。',
      body: '语言教育培养在学校或成人环境中教授阅读、写作、口语及聆听的能力。您将结合语言能力、教学方法及学习支持。若您善于清晰讲解，并乐于见证他人进步，此方向较为合适。',
      paths: '语言教育学士 · 英语或中文作为第二语言教学 · 课程设计及语言中心相关岗位'
    },
    20: {
      tagline: '记录财务、把关数字，保障机构稳健运作。',
      body: '会计旨在衡量表现、准确申报及支持稳健财务决策。您将建立分析、审计及专业准则方面的能力，备受雇主重视。若您细致严谨，并乐于处理数字与规则，会计提供相对稳定的职业路径。',
      paths: '会计学士 · 专业会计资格进修 · 审计、税务及财务申报相关岗位'
    },
    21: {
      tagline: '以运动、体能及教练工作训练身心。',
      body: '体育与教练结合运动科学、动力、团队合作及表现提升。您可能与运动员、学校、球会或社区健身计划合作。若您喜爱运动、具备纪律，并乐于协助他人达成目标，此领域富有活力。',
      paths: '运动科学或体育教育学士 · 教练及健身指导 · 体育管理及运动员发展相关路径'
    },
    22: {
      tagline: '将数据转化为洞察，以软件解决问题。',
      body: '数据与运算涵盖编程、数据库、分析及机构日常依赖的系统。您将以程序解难，并理解大型数据集。若您喜爱逻辑、试验及构建数字工具，此方向灵活性较高。',
      paths: '计算机科学学士 · 数据分析及信息系统 · 软件开发及 IT 支援相关岗位'
    },
    23: {
      tagline: '深入学习健康，从诊疗到配药全面支持。',
      body: '医学与药学需要扎实的科学基础及长期投入患者福祉。您可能诊断疾病、处方治疗，或确保药物安全有效。若您已准备好接受严格训练及承担高度责任，此群组值得认真考虑。',
      paths: '医学或药学学士 · 临床前健康科学课程 · 临床、药物及医疗专科培训'
    },
    24: {
      tagline: '管理资金与风险，把握重塑金融的科技。',
      body: '金融与 FinTech 结合投资、银行及移动支付、区块链等数字工具。您将学习评估风险、建模回报及理解市场变化。若您喜爱快节奏的数字工作、策略及创新，往往倾向此领域。',
      paths: '金融或金融科技学士 · 投资及风险管理 · 银行、财富管理及 FinTech 产品相关岗位'
    },
    25: {
      tagline: '深化中文、文化及人文素养。',
      body: '中文与人文探索文学、历史、语言及文化传统。您将建立扎实的阅读、写作及诠释能力，适用于教育、媒体及公共生活。若您喜爱古典与现代文本，并关注身份与社会，此路径可能较为契合。',
      paths: '中国语言文学学士 · 文化研究及人文研究 · 出版、教育及文化项目相关岗位'
    },
    26: {
      tagline: '以生命科学改善健康、诊断及医学研究。',
      body: '生物医学与健康科学连接生物与医学，涵盖基因、疾病机制及健康科技。您可能在研究实验室、诊断或健康产业工作。若您充满好奇心、喜爱实验，并希望为健康成果作出贡献，此领域较为合适。',
      paths: '生物医学科学学士 · 临床研究及实验室岗位 · 公共卫生及健康科学研究院进修'
    },
    27: {
      tagline: '保护生态，应对气候及可持续发展挑战。',
      body: '环境科学研究空气、水、土地及生物多样性，以及人类活动的影响。您将结合野外工作、数据及政策意识，提出可行方案。若您关注地球，并重视具有实际影响的科学，此领域意义深远。',
      paths: '环境科学学士 · 保育及可持续发展课程 · 环境顾问及政策支援相关岗位'
    },
    28: {
      tagline: '研究社会，推动更公平的公共决策。',
      body: '社会学与公共政策检视不平等、制度及政府如何回应社会需要。您将学习研究方法及如何评估影响社群的计划。若您关注社会正义及循证改变，此领域通常颇具吸引力。',
      paths: '社会学或公共政策学士 · 社会研究及计划评估 · 政府、非政府组织及倡议相关岗位'
    },
    29: {
      tagline: '讲述故事、传递信息，触达各平台受众。',
      body: '媒体与传播涵盖新闻、公关、数字内容及信息在社会中的流通。您将培养写作、制作及策略沟通能力。若您喜爱创意及时事，并希望通过文字与影像与人连接，可探索此方向。',
      paths: '新闻或传播学学士 · 公共关系及内容策略 · 广播、社交媒体及企业传讯相关岗位'
    },
    30: {
      tagline: '以视觉艺术、电影及创作表达理念。',
      body: '纯艺术与电影培养绘画、雕塑、摄影及以影像与动态叙事的能力。您将建立作品集，并学习技艺、评论及协作。若您视觉感强，并希望有空间试验及发展个人风格，此路径值得考虑。',
      paths: '纯艺术或电影学士 · 视觉艺术及摄影课程 · 画廊、工作室及创作制作相关岗位'
    },
    31: {
      tagline: '结合工程与医学，研发健康科技及装置。',
      body: '生物医学工程设计植入物、影像工具、义肢等支持诊断及治疗的系统。您将运用数学、生物及工程解决临床问题。若您乐于在健康与科技前沿发明实用方案，此领域往往较为合适。',
      paths: '生物医学工程学士 · 医疗装置及影像科技 · 康复工程及健康科技研究相关路径'
    },
    32: {
      tagline: '设计可靠、可扩展的复杂系统。',
      body: '系统工程专注于将人、硬件、软件及流程整合为稳定的整体方案。您需从头到尾考虑性能、安全及生命周期。若您善于宏观规划，并擅长令多个环节协同运作，此领域较为契合。',
      paths: '系统工程学士 · 工业及系统设计 · 航空、运输及大型工程项目相关岗位'
    },
    33: {
      tagline: '构建会学习、预测及自动化决策的智能系统。',
      body: '人工智能运用数学、运算及数据，使机器识别模式并协助人类工作。您可能从事机器学习、自然语言或机器人应用。若您喜爱编程、试验及前沿科技，此方向动态性较强。',
      paths: '人工智能或计算机科学学士 · 机器学习及数据科学课程 · AI 产品、研究及自动化工程相关岗位'
    },
    34: {
      tagline: '掌握语言，开启跨文化及多元路径。',
      body: '英语与语言培养进阶阅读、写作、翻译及跨文化沟通能力。您可能学习语言学、文学或职场专业语言运用。若您喜爱文字及细微差别，并希望与不同背景的人连接，此群组选择广泛。',
      paths: '英语或现代语言学士 · 翻译及传译课程 · 国际关系、教育及出版相关路径'
    }
  }
};

const SUPPORTED_LANGS = /** @type {const} */ (['en', 'tc', 'sc']);
const DEFAULT_LANG = 'en';
const CLUSTER_COUNT = 35;

/**
 * Popup copy for an interest cluster tile (tagline, body, example paths).
 * @param {number | string} clusterId 0–34
 * @param {'en'|'tc'|'sc' | string} [lang]
 * @returns {InterestClusterPopupEntry | null}
 */
export function getInterestClusterPopupCopy(clusterId, lang = DEFAULT_LANG) {
  const id = Number(clusterId);
  if (!Number.isFinite(id) || id < 0 || id >= CLUSTER_COUNT) {
    return null;
  }

  const locale = SUPPORTED_LANGS.includes(/** @type {'en'|'tc'|'sc'} */ (lang))
    ? /** @type {'en'|'tc'|'sc'} */ (lang)
    : DEFAULT_LANG;

  return INTEREST_CLUSTER_POPUP[locale]?.[id] ?? INTEREST_CLUSTER_POPUP[DEFAULT_LANG]?.[id] ?? null;
}
